import {TKeyResult, useKeyResult} from '../../../hooks';
import {memo, ReactElement} from 'react';
import styled from 'styled-components';
import {VerticalSpacer} from '../../atoms/spacer';
import {AddMoreAction} from '../../molecules/add-more';
import {Helper} from '../../atoms/helper';
import {Fragment} from 'react';
import {KeyResultField} from '../../molecules/key-result-field';
import {Label} from '../../atoms/label';
import {Body2, Headline4} from '../../atoms/typography';
import {TMemberOption} from '../../molecules/key-result-field/key-result-field';
import {DeepMap, FieldError} from 'react-hook-form';
import {computeFrameworkVars} from '../../../utils/framework';
import {authStore} from '../../../store/stores/auth-store';
import {InputState} from '../../interface';
import {FlexRow} from '@ui/style/styles';

const Wrapper = styled.div`
  width: 100%;
`;

const CursorWrapper = styled.div`
  position: relative;

  > span {
    position: relative;
    bottom: 0;
  }
`;

const CreateGoalHeader = styled.div`
  display: flex;
  width: 100%;
  flex-direction: column;
  gap: 4px;
  margin-bottom: 16px;
`;

export interface EmailsInvitationProps {
  setValue?: (values: TKeyResult[]) => void;
  defaultValue: TKeyResult[];
  options: TMemberOption[];
  errors?: (DeepMap<TKeyResult, FieldError> | undefined)[] | undefined;
  state?: InputState;
  helper?: string | ReactElement;
  variant?: 'default' | 'createGoal';
  compact?: boolean;
}

export const KeyResults = memo(function ({
  setValue,
  defaultValue,
  options,
  errors,
  state,
  helper,
  variant = 'default',
  compact,
}: EmailsInvitationProps) {
  const {
    addKeyresult,
    keyresults,
    onChangeKeyResult,
    removeKeyresult,
    disabled,
  } = useKeyResult(defaultValue, setValue);
  const configuredKeyResultName =
    authStore?.auth?.user?.workspace?.config?.keyResultName ||
    computeFrameworkVars(authStore?.auth?.user?.workspace?.framework)?.k_rs ||
    'Key Results';
  const keyResultHeading =
    configuredKeyResultName.charAt(0).toUpperCase() +
    configuredKeyResultName.slice(1);

  return (
    <Wrapper>
      {variant === 'createGoal' ? (
        <CreateGoalHeader>
          <Headline4 style={{margin: 0}}>{keyResultHeading}</Headline4>
          <Body2 kind="textBody">
            The measurable results that show progress on this goal. Add up to 5.
          </Body2>
        </CreateGoalHeader>
      ) : (
        <FlexRow className="mb-2">
          <Label>
            What's a{' '}
            {
              computeFrameworkVars(authStore?.auth?.user?.workspace?.framework)
                ?.k_r
            }{' '}
            to track this with?
          </Label>
        </FlexRow>
      )}
      {Array.from(keyresults).map(
        ([key, {keyresult}], index, allKeyResults) => (
          <Fragment key={key}>
            {!keyresult.id ? (
              <>
                <KeyResultField
                  key={key}
                  setValue={(results: TKeyResult) => {
                    onChangeKeyResult(key, results);
                  }}
                  index={index}
                  members={options}
                  size={allKeyResults.length}
                  value={{
                    measurement: {
                      unit: keyresult.measurement.unit,
                      symbol: keyresult.measurement.symbol,
                    },
                    name: keyresult.name,
                    range: keyresult.range,
                    source: keyresult.source,
                    showSource: keyresult?.showSource,
                    saveSource: keyresult?.saveSource,
                    user: keyresult.user,
                  }}
                  onRemove={() => removeKeyresult(key)}
                  name={key}
                  // @ts-ignore
                  errors={errors && errors[index]}
                  variant={variant}
                  compact={compact}
                />
              </>
            ) : (
              <>
                <KeyResultField
                  key={key}
                  index={index}
                  size={allKeyResults.length}
                  setValue={(results: TKeyResult) => {
                    onChangeKeyResult(key, results);
                  }}
                  members={options}
                  value={{
                    id: keyresult?.id,
                    measurement: {
                      unit: keyresult?.measurement?.unit,
                      symbol: keyresult?.measurement?.symbol,
                    },
                    name: keyresult.name,
                    range: keyresult?.range,
                    showSource: keyresult?.showSource,
                    saveSource: keyresult?.saveSource,
                    user: keyresult?.user,
                    source: keyresult.source,
                  }}
                  onRemove={() => removeKeyresult(key)}
                  name={key}
                  // @ts-ignore
                  errors={errors && errors[index]}
                  variant={variant}
                  compact={compact}
                />
              </>
            )}
            <VerticalSpacer size={variant === 'createGoal' ? '16px' : '18px'} />
          </Fragment>
        ),
      )}

      {disabled && (
        <CursorWrapper>
          <Helper
            children={`You can add up to 5 ${configuredKeyResultName} at once`}
            state="warning"
          />
        </CursorWrapper>
      )}
      <VerticalSpacer size={variant === 'createGoal' ? '0px' : '8px'} />
      <AddMoreAction
        onClick={addKeyresult}
        disabled={disabled}
        title={`Add ${
          variant === 'createGoal'
            ? 'result'
            : computeFrameworkVars(authStore?.auth?.user?.workspace?.framework)
                ?.k_r
        }`}
      />
      <Helper aria-label="helper" state={state} children={helper} />
    </Wrapper>
  );
});
KeyResults.displayName = 'KeyResults';
